import { useState } from "react";
import { CircleHelp, ExternalLink, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { useBackfillResourceField } from "@/hooks/useResourceQueries";
import {
  RESOURCE_BACKFILL_FIELDS,
  type ResourceBackfillField,
} from "@/types/admin";

/**
 * What the operator reads next to each field. A Record over the field union, so a field added to
 * `RESOURCE_BACKFILL_FIELDS` without a line here is a compile error rather than a blank row.
 */
const FIELD_COPY: Record<
  ResourceBackfillField,
  { label: string; description: string }
> = {
  ProductLineSlug: {
    label: "Product line",
    description:
      "VMs created before the Product Line filter existed are not found by it. This gives each of them the product line already recorded in its specifications.",
  },
};

type FieldRun =
  | { status: "running" }
  | { status: "done"; scannedCount: number; updatedCount: number }
  | { status: "failed"; message: string };

/**
 * A card of the Helper page (SuperAdmin only; a page of its own at /admin/resources/backfill until
 * 2026-10-13). Fills derived resource columns for the rows saved before those columns existed —
 * the step an operator runs once after a release that adds such a column. The fields offered are
 * exactly the ones the backend can fill, each with its own Update button.
 *
 * A run writes only rows whose field is still empty and changes nothing else about a resource, so
 * running a field twice does no harm. It still asks first (2026-10-09): one click walks every
 * resource in the table, and the button sits where a stray click can reach it.
 */
export function UpdateResourceFieldsCard() {
  const backfillField = useBackfillResourceField();
  const [runs, setRuns] = useState<
    Partial<Record<ResourceBackfillField, FieldRun>>
  >({});
  // One field at a time: each run pages through the whole table, and two of them would write the
  // same rows.
  const isRunning = RESOURCE_BACKFILL_FIELDS.some(
    (field) => runs[field]?.status === "running",
  );

  const [fieldToConfirm, setFieldToConfirm] =
    useState<ResourceBackfillField | null>(null);

  const run = async (field: ResourceBackfillField) => {
    setRuns((current) => ({ ...current, [field]: { status: "running" } }));

    try {
      const result = await backfillField.mutateAsync(field);
      setRuns((current) => ({
        ...current,
        [field]: {
          status: "done",
          scannedCount: result.scannedCount,
          updatedCount: result.updatedCount,
        },
      }));
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "An unexpected error occurred";
      setRuns((current) => ({
        ...current,
        [field]: { status: "failed", message },
      }));
    }
  };

  // The dialog closes itself on confirm; the row then reports the run.
  const confirmRun = () => {
    if (fieldToConfirm) void run(fieldToConfirm);
  };

  return (
    <Card className="shadow-none">
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 p-4 pb-3">
        <CardTitle className="text-base">Update resource fields</CardTitle>
        <HowThisWorks />
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <ul className="divide-y rounded-md border">
          {RESOURCE_BACKFILL_FIELDS.map((field) => {
            const copy = FIELD_COPY[field];
            const fieldRun = runs[field];
            const isFieldRunning = fieldRun?.status === "running";

            return (
              <li
                key={field}
                className="flex flex-wrap items-center gap-3 p-3 sm:flex-nowrap"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{copy.label}</p>
                  <p className="text-sm text-muted-foreground">
                    {copy.description}
                  </p>
                </div>
                <div className="shrink-0 text-sm" aria-live="polite">
                  {fieldRun?.status === "done" && fieldRun.updatedCount > 0 && (
                    <Badge variant="outline" className="whitespace-nowrap">
                      {fieldRun.updatedCount} of {fieldRun.scannedCount} updated
                    </Badge>
                  )}
                  {fieldRun?.status === "done" &&
                    fieldRun.updatedCount === 0 && (
                      <Badge variant="outline" className="whitespace-nowrap">
                        Nothing to update
                      </Badge>
                    )}
                  {fieldRun?.status === "failed" && (
                    <span className="text-destructive">
                      Failed: {fieldRun.message}
                    </span>
                  )}
                </div>
                <Button
                  size="sm"
                  className="shrink-0"
                  aria-label={`Update ${copy.label}`}
                  disabled={isRunning}
                  onClick={() => setFieldToConfirm(field)}
                >
                  {isFieldRunning && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isFieldRunning ? "Updating…" : "Update"}
                </Button>
              </li>
            );
          })}
        </ul>
      </CardContent>

      <ConfirmDialog
        open={fieldToConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setFieldToConfirm(null);
        }}
        title={
          fieldToConfirm
            ? `Update "${FIELD_COPY[fieldToConfirm].label}" on existing resources?`
            : ""
        }
        description={
          fieldToConfirm
            ? `${FIELD_COPY[fieldToConfirm].description} Only resources whose field is still empty are written; nothing else about a resource changes.`
            : ""
        }
        confirmText="Update"
        onConfirm={confirmRun}
      />
    </Card>
  );
}

/**
 * The card's own instructions, one click away rather than a paragraph on every visit. The long
 * version, field by field, is `/docs/resource-field-update.html`.
 */
function HowThisWorks() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 min-h-8 shrink-0 gap-2 px-2 text-sm"
        >
          <CircleHelp className="h-4 w-4" />
          How this works
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[420px] text-sm">
        <h3 className="mb-2 font-semibold">How this works</h3>
        <ol className="list-decimal space-y-2 pl-4 text-muted-foreground">
          <li>
            Each row is a field that was added after resources already existed.{" "}
            <span className="text-foreground">Update</span> fills it for those
            older resources. Run it once after a release that adds a field.
          </li>
          <li>
            Only resources whose field is{" "}
            <span className="text-foreground">still empty</span> are written.
            Nothing else about a resource changes, and deleted resources are
            left as they are.
          </li>
          <li>
            One field runs at a time. It asks first, then goes through every
            resource, so the other buttons wait until it finishes.
          </li>
          <li>
            The result reads{" "}
            <span className="text-foreground">N of M updated</span>: M resources
            had the field empty and N of them got a value. The rest have no
            value recorded to copy and stay empty.
          </li>
          <li>
            Running a field again is safe: it fills nothing more and says{" "}
            <span className="text-foreground">Nothing to update</span>. A run
            that fails keeps what it already filled; run it again.
          </li>
        </ol>

        <Button variant="outline" size="sm" asChild className="mt-3 w-full">
          <a
            href="/docs/resource-field-update.html"
            target="_blank"
            rel="noreferrer"
          >
            Full guide
            <ExternalLink className="ml-2 h-3.5 w-3.5" />
          </a>
        </Button>
      </PopoverContent>
    </Popover>
  );
}
